/**
 * 报名详情抽屉（单条兼容路径 + 归属报名单，enrollment-submission-source-alignment:SC-38）。
 *
 * 只读展示关键字段（含学生档案派生：性别/年级学段/分数/监护人）+ 派生转化状态 +
 * 试课/课时余额；关联订单可点开详情；转订单仅可转化时显示。
 */
import { useEffect, useRef, useState } from "react";
import { useHasPermission } from "../../auth/HasPermission";
import { Button, Descriptions, Drawer, Space, Tag, Typography } from "antd";
import {
  enrollmentsApi,
  CONVERSION_STATUS_LABEL,
  type EnrollmentItem,
  type EnrollmentSubmissionDetail,
} from "../../api/enrollments.api";
import { studentsApi, type StudentDetail } from "../../api/students.api";
import { billingApi } from "../../api/billing.api";
import { genderLabel, gradeStageLabel } from "../../api/labels";
import { ApiError } from "../../api/transport";
import { EnrollmentEditModal } from "./EnrollmentEditModal";
import { fmtDateTime } from "../../utils/datetime";
import { ConvertOrderModal } from "./ConvertOrderModal";
import { OrderDetailDrawer } from "../billing/OrderDetailDrawer";
import { MaskedMobile } from "./MaskedMobile";

export interface EnrollmentDetailDrawerProps {
  applicationId: string | null;
  onClose: () => void;
  onChanged: () => void;
  /**
   * 报名单不存在（404）时回调，调用方可切换为回退说明（如账本业务编号下钻 SC-16）。
   * 不传时保持原行为（抽屉打开但内容为空）。
   */
  onNotFound?: () => void;
}

const CONVERSION_COLOR: Record<string, string> = {
  PENDING_TRIAL: "blue",
  IN_TRIAL: "geekblue",
  TRIAL_ENDED_FOLLOW_UP: "orange",
  CONVERTED_PENDING_PAYMENT: "gold",
  CONVERTED_PAID: "green",
};

const SOURCE_LABEL: Record<string, string> = {
  STAFF_ASSISTED: "员工代建",
  PARENT_SELF: "家长自助",
  SHARE_REFERRAL: "好友分享",
};

export function EnrollmentDetailDrawer({
  applicationId,
  onClose,
  onChanged,
  onNotFound,
}: EnrollmentDetailDrawerProps) {
  const [item, setItem] = useState<EnrollmentItem | null>(null);
  const [group, setGroup] = useState<EnrollmentSubmissionDetail | null>(null);
  const [student, setStudent] = useState<StudentDetail | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [viewingOrderId, setViewingOrderId] = useState<string | null>(null);
  // onNotFound 经 ref 转发，调用方可传内联函数而不重触发加载
  const notFoundRef = useRef(onNotFound);
  notFoundRef.current = onNotFound;

  useEffect(() => {
    if (!applicationId) return;
    setGroup(null);
    setStudent(null);
    enrollmentsApi
      .getEnrollment(applicationId)
      .then((r) => {
        setItem(r);
        if (r?.submissionId) {
          enrollmentsApi
            .getEnrollmentSubmission(r.submissionId)
            .then(setGroup)
            .catch(() => setGroup(null));
        }
        if (r?.studentId) {
          studentsApi
            .getStudent(r.studentId)
            .then(setStudent)
            .catch(() => setStudent(null));
        }
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 404) {
          notFoundRef.current?.();
          return;
        }
        setItem(null);
      });
  }, [applicationId]);

  const canConvert =
    !!item &&
    item.conversionStatus !== "CONVERTED_PENDING_PAYMENT" &&
    item.conversionStatus !== "CONVERTED_PAID";
  // 编辑=ADM-02 EDIT（PATCH 报名）、转订单=ADM-09 CREATE（建订单端点），对齐后端 @Roles
  const canEditEnrollment = useHasPermission("ADM-02", "EDIT");
  const canCreateOrder = useHasPermission("ADM-09", "CREATE");

  const openLinkedOrder = async () => {
    if (!item?.orderNo) return;
    try {
      const page = await billingApi.listOrders({
        studentId: item.studentId,
        page: 1,
        pageSize: 50,
      });
      const found = page.items.find((o) => o.orderNo === item.orderNo);
      if (found) setViewingOrderId(found.orderId);
    } catch {
      // 订单详情加载失败时保持报名抽屉
    }
  };

  const guardian = student?.guardians?.[0];

  return (
    <Drawer
      title={`报名详情${item ? ` · ${item.studentName}` : ""}`}
      open={!!applicationId}
      onClose={onClose}
      width={480}
      extra={
        item && (
          <Space>
            {canEditEnrollment && (
              <Button onClick={() => setEditOpen(true)}>编辑</Button>
            )}
            {canConvert && canCreateOrder && (
              <Button type="primary" onClick={() => setConvertOpen(true)}>
                转订单
              </Button>
            )}
            {item.orderNo && (
              <Button onClick={() => void openLinkedOrder()}>订单</Button>
            )}
          </Space>
        )
      }
    >
      {item && (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="报名编号">
            <Typography.Text copyable={{ text: item.applicationId }}>
              #{item.applicationId.slice(0, 8)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="学生">{item.studentName}</Descriptions.Item>
          <Descriptions.Item label="性别">
            {student ? (genderLabel[student.gender] ?? student.gender) : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="年级/学段">
            {student
              ? (gradeStageLabel[student.gradeStage] ?? student.gradeStage)
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="科目分数">
            {student?.subjectScores?.length
              ? student.subjectScores
                  .map((s) => `${s.subjectName}:${s.score}`)
                  .join("；")
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="监护人">
            {guardian?.name ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="监护人手机号">
            {student ? (
              <MaskedMobile
                studentId={student.id}
                masked={student.guardianMobile}
              />
            ) : (
              "—"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="课程 SKU">
            {item.skuName}（SKU 与校区不可改）
          </Descriptions.Item>
          <Descriptions.Item label="校区">{item.campusName}</Descriptions.Item>
          <Descriptions.Item label="报名单号">
            <Typography.Text copyable={{ text: item.submissionId }}>
              #{item.submissionId.slice(0, 8)}
            </Typography.Text>
          </Descriptions.Item>
          <Descriptions.Item label="来源商品">
            {group?.sourceProductName ??
              (item.sourceProductId ? "加载中…" : "—（无来源商品）")}
          </Descriptions.Item>
          <Descriptions.Item label="报名来源">
            {SOURCE_LABEL[item.source] ?? item.source}
          </Descriptions.Item>
          <Descriptions.Item label="顾问">
            {item.consultantName ?? "—"}
          </Descriptions.Item>
          <Descriptions.Item label="赠送课时次数">
            {item.trialGranted}
          </Descriptions.Item>
          <Descriptions.Item label="赠送课剩余 / 正式课剩余">
            {item.giftedBalance} / {item.formalBalance}
          </Descriptions.Item>
          <Descriptions.Item label="转化状态">
            <Tag color={CONVERSION_COLOR[item.conversionStatus]}>
              {CONVERSION_STATUS_LABEL[item.conversionStatus]}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="关联订单号">
            {item.orderNo ? (
              <Typography.Link onClick={() => void openLinkedOrder()}>
                {item.orderNo}
              </Typography.Link>
            ) : (
              "暂未转订单"
            )}
          </Descriptions.Item>
          <Descriptions.Item label="备注">{item.note ?? "—"}</Descriptions.Item>
          <Descriptions.Item label="提交时间">
            {fmtDateTime(item.submittedAt)}
          </Descriptions.Item>
        </Descriptions>
      )}
      {item && (
        <>
          <EnrollmentEditModal
            open={editOpen}
            item={item}
            onClose={() => setEditOpen(false)}
            onSaved={() => {
              setEditOpen(false);
              enrollmentsApi.getEnrollment(item.applicationId).then(setItem);
              studentsApi
                .getStudent(item.studentId)
                .then(setStudent)
                .catch(() => undefined);
              onChanged();
            }}
          />
          <ConvertOrderModal
            open={convertOpen}
            submissionId={item.submissionId}
            onClose={() => setConvertOpen(false)}
          />
          {viewingOrderId && (
            <OrderDetailDrawer
              orderId={viewingOrderId}
              onClose={() => setViewingOrderId(null)}
            />
          )}
        </>
      )}
    </Drawer>
  );
}
